import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input } from '@theraply/ui';
import { EnvelopeIcon, PhoneIcon, LockClosedIcon, UserIcon } from '@heroicons/react/24/outline';
import { BasicInfoData } from '../../../hooks/useRegistrationWizard';

interface BasicInfoStepProps {
  form: UseFormReturn<BasicInfoData>;
  isNewRegistration?: boolean;
  prefilledEmail?: string;
}

export function BasicInfoStep({ form, isNewRegistration = true, prefilledEmail }: BasicInfoStepProps) {
  const {
    register,
    formState: { errors },
    setValue,
  } = form;

  // Pre-fill email if provided (e.g., from authentication)
  useEffect(() => {
    if (prefilledEmail) {
      setValue('email', prefilledEmail);
    }
  }, [prefilledEmail, setValue]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Basic Information</h2>
        <p className="mt-2 text-sm text-gray-600">
          Let's start with your basic contact information.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <Input
            {...register('firstName')}
            label="First Name"
            placeholder="John"
            required
            error={errors.firstName?.message}
            startIcon={<UserIcon className="h-5 w-5" />}
            fullWidth
          />
        </div>

        <div>
          <Input
            {...register('lastName')}
            label="Last Name"
            placeholder="Doe"
            required
            error={errors.lastName?.message}
            startIcon={<UserIcon className="h-5 w-5" />}
            fullWidth
          />
        </div>
      </div>

      <div>
        <Input
          {...register('email')}
          type="email"
          label="Email Address"
          placeholder="john.doe@example.com"
          required
          error={errors.email?.message}
          startIcon={<EnvelopeIcon className="h-5 w-5" />}
          fullWidth
          disabled={!!prefilledEmail}
          helperText={prefilledEmail ? 'Email is pre-filled from your account' : undefined}
        />
      </div>

      <div>
        <Input
          {...register('phone')}
          type="tel"
          label="Phone Number"
          placeholder="+12025551234"
          required
          error={errors.phone?.message}
          startIcon={<PhoneIcon className="h-5 w-5" />}
          helperText="Format: +1 followed by 10 digits (E.164 format)"
          fullWidth
        />
      </div>

      {isNewRegistration && (
        <>
          <div>
            <Input
              {...register('password')}
              type="password"
              label="Password"
              placeholder="Enter a secure password"
              required={isNewRegistration}
              error={errors.password?.message}
              startIcon={<LockClosedIcon className="h-5 w-5" />}
              helperText="Must be at least 8 characters"
              fullWidth
            />
          </div>

          <div>
            <Input
              {...register('confirmPassword')}
              type="password"
              label="Confirm Password"
              placeholder="Re-enter your password"
              required={isNewRegistration}
              error={errors.confirmPassword?.message}
              startIcon={<LockClosedIcon className="h-5 w-5" />}
              fullWidth
            />
          </div>
        </>
      )}

      <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Information Privacy</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Your contact information is securely stored and will only be used for account
                verification and communication purposes. We never share your personal information
                with third parties.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
